import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Check, ArrowLeft, ShoppingCart, User, CreditCard } from 'lucide-react';

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Get plan details from URL params
  const planName = searchParams.get('plan');
  const amount = searchParams.get('amount');
  const serviceName = searchParams.get('service') || 'Service Package';
  
  // Customer information
  const [customerInfo, setCustomerInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });
  
  const [errors, setErrors] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'paypal'
  
  // Card details
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });
  
  useEffect(() => {
    // Redirect if no plan selected
    if (!planName || !amount) {
      navigate('/');
    }
  }, [planName, amount, navigate]);
  
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!customerInfo.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!customerInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!customerInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!customerInfo.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (!customerInfo.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!customerInfo.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    if (!customerInfo.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleCardInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format card number (add spaces every 4 digits)
    if (name === 'cardNumber') {
      const formatted = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      setCardDetails(prev => ({ ...prev, [name]: formatted }));
    }
    // Format expiry date (MM/YY)
    else if (name === 'expiryDate') {
      const formatted = value.replace(/\D/g, '').replace(/(\d{2})(\d{0,2})/, '$1/$2');
      setCardDetails(prev => ({ ...prev, [name]: formatted }));
    }
    // Format CVV (max 4 digits)
    else if (name === 'cvv') {
      const formatted = value.replace(/\D/g, '').slice(0, 4);
      setCardDetails(prev => ({ ...prev, [name]: formatted }));
    }
    else {
      setCardDetails(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleCardPayment = () => {
    // Validate card details
    if (!cardDetails.cardNumber || !cardDetails.cardName || !cardDetails.expiryDate || !cardDetails.cvv) {
      toast({
        title: 'Card Details Required',
        description: 'Please fill in all card information.',
        variant: 'destructive',
      });
      return;
    }
    
    // Show message that API is needed
    toast({
      title: 'Payment Processing Unavailable',
      description: 'Card payment will be available once API credentials are configured.',
      variant: 'destructive',
    });
  };
  
  const handleNextStep = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };
  
  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // PayPal integration
  const createOrder = async () => {
    try {
      setLoading(true);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/paypal/create-order`,
        {
          plan_name: planName,
          amount: parseFloat(amount),
          currency: 'USD',
          customer_email: customerInfo.email,
          customer_name: customerInfo.fullName,
          service_name: serviceName
        }
      );
      
      setLoading(false);
      return response.data.id;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      toast({
        title: 'Payment Error',
        description: 'Failed to initiate payment. Please try again.',
        variant: 'destructive',
      });
      setLoading(false);
      return null;
    }
  };
  
  const onApprove = async (data) => {
    try {
      setLoading(true);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/paypal/capture-order`,
        {
          order_id: data.orderID
        }
      );
      
      toast({
        title: 'Payment Successful!',
        description: 'Thank you for your purchase. We will contact you shortly.',
      });
      
      // Redirect to success page
      setTimeout(() => {
        navigate(`/payment-success?orderId=${data.orderID}`);
      }, 2000);
      
      setLoading(false);
    } catch (error) {
      console.error('Error capturing PayPal order:', error);
      toast({
        title: 'Payment Capture Failed',
        description: 'There was an issue processing your payment. Please contact support.',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };
  
  const onError = (err) => {
    console.error('PayPal Error:', err);
    toast({
      title: 'Payment Error',
      description: 'An error occurred during payment. Please try again.',
      variant: 'destructive',
    });
  };
  
  const steps = [
    { number: 1, title: 'Customer Info', icon: User },
    { number: 2, title: 'Review Order', icon: ShoppingCart },
    { number: 3, title: 'Payment', icon: CreditCard }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pt-32 pb-20">
      <div className="container mx-auto px-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>
        
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-2 transition-all duration-300 ${
                      currentStep >= step.number
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                        : 'bg-white text-gray-400 border-2 border-gray-300'
                    }`}>
                      <step.icon className="w-8 h-8" />
                    </div>
                    <p className={`text-sm font-medium ${
                      currentStep >= step.number ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 transition-all duration-300 ${
                      currentStep > step.number ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          
          <Card className="shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8">
              <h1 className="text-3xl font-bold">Checkout</h1>
              <p className="text-blue-100 mt-2">Complete your purchase in just a few steps</p>
            </CardHeader>
            
            <CardContent className="p-8">
              {/* Step 1: Customer Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Information</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={customerInfo.fullName}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className={errors.fullName ? 'border-red-500' : ''}
                      />
                      {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={customerInfo.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={customerInfo.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (555) 123-4567"
                        className={errors.phone ? 'border-red-500' : ''}
                      />
                      {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        name="country"
                        value={customerInfo.country}
                        onChange={handleInputChange}
                        readOnly
                        className="bg-gray-50"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={customerInfo.address}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                      className={errors.address ? 'border-red-500' : ''}
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={customerInfo.city}
                        onChange={handleInputChange}
                        placeholder="Los Angeles"
                        className={errors.city ? 'border-red-500' : ''}
                      />
                      {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={customerInfo.state}
                        onChange={handleInputChange}
                        placeholder="CA"
                        className={errors.state ? 'border-red-500' : ''}
                      />
                      {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="zipCode">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={customerInfo.zipCode}
                        onChange={handleInputChange}
                        placeholder="90025"
                        className={errors.zipCode ? 'border-red-500' : ''}
                      />
                      {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-8">
                    <Button
                      onClick={handleNextStep}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-full text-lg"
                    >
                      Continue to Review
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 2: Review Order */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Review Your Order</h2>
                  
                  <div className="bg-blue-50 rounded-xl p-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Service:</span>
                        <span className="font-semibold">{serviceName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Plan:</span>
                        <span className="font-semibold">{planName}</span>
                      </div>
                      <div className="border-t border-blue-200 pt-3 mt-3 flex justify-between text-xl">
                        <span className="font-bold text-gray-900">Total:</span>
                        <span className="font-bold text-blue-600">${amount}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Billing Information</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><span className="font-semibold">Name:</span> {customerInfo.fullName}</p>
                      <p><span className="font-semibold">Email:</span> {customerInfo.email}</p>
                      <p><span className="font-semibold">Phone:</span> {customerInfo.phone}</p>
                      <p><span className="font-semibold">Address:</span> {customerInfo.address}</p>
                      <p><span className="font-semibold">City:</span> {customerInfo.city}, {customerInfo.state} {customerInfo.zipCode}</p>
                      <p><span className="font-semibold">Country:</span> {customerInfo.country}</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between mt-8">
                    <Button
                      onClick={handlePreviousStep}
                      variant="outline"
                      className="px-8 py-3 rounded-full text-lg"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleNextStep}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-full text-lg"
                    >
                      Proceed to Payment
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 3: Payment */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Method</h2>
                  
                  <div className="bg-blue-50 rounded-xl p-6 mb-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-gray-700">Total Amount</p>
                        <p className="text-3xl font-bold text-blue-600">${amount}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{planName}</p>
                        <p className="text-sm text-gray-500">{serviceName}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <p className="text-center text-gray-700 font-medium text-lg">Choose your payment method</p>
                    
                    {/* Payment Method Selection */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Credit/Debit Card Option */}
                      <button
                        onClick={() => setPaymentMethod('card')}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                          paymentMethod === 'card'
                            ? 'border-blue-600 bg-blue-50 shadow-lg'
                            : 'border-gray-300 bg-white hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center justify-center mb-3">
                          <CreditCard className={`w-10 h-10 ${paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                        <p className={`text-lg font-bold ${paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-700'}`}>
                          Credit/Debit Card
                        </p>
                        <p className="text-sm text-gray-600 mt-2">Pay with Visa, Mastercard, Amex</p>
                      </button>
                      
                      {/* PayPal Option */}
                      <button
                        onClick={() => setPaymentMethod('paypal')}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                          paymentMethod === 'paypal'
                            ? 'border-blue-600 bg-blue-50 shadow-lg'
                            : 'border-gray-300 bg-white hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center justify-center mb-3">
                          <svg className={`w-10 h-10 ${paymentMethod === 'paypal' ? 'text-blue-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l1.086-6.874h2.19c5.074 0 8.292-2.476 9.477-7.201z"/>
                          </svg>
                        </div>
                        <p className={`text-lg font-bold ${paymentMethod === 'paypal' ? 'text-blue-600' : 'text-gray-700'}`}>
                          PayPal
                        </p>
                        <p className="text-sm text-gray-600 mt-2">Pay with your PayPal account</p>
                      </button>
                    </div>
                    
                    {/* Payment Form Based on Selection */}
                    <div className="mt-8">
                      <div className="max-w-md mx-auto space-y-4">
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                            <CreditCard className="w-6 h-6 mr-2 text-blue-600" />
                            Payment Details
                          </h3>
                          
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="paymentName">Full Name</Label>
                              <Input
                                id="paymentName"
                                name="paymentName"
                                value={cardDetails.cardName}
                                onChange={(e) => setCardDetails(prev => ({ ...prev, cardName: e.target.value }))}
                                placeholder="John Doe"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="paymentEmail">Email</Label>
                              <Input
                                id="paymentEmail"
                                name="paymentEmail"
                                type="email"
                                value={customerInfo.email}
                                readOnly
                                className="bg-gray-50"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="cardNumber">Card Number</Label>
                              <Input
                                id="cardNumber"
                                name="cardNumber"
                                value={cardDetails.cardNumber}
                                onChange={handleCardInputChange}
                                placeholder="1234 5678 9012 3456"
                                maxLength="19"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="expiryDate">Expiry (MM/YY)</Label>
                                <Input
                                  id="expiryDate"
                                  name="expiryDate"
                                  value={cardDetails.expiryDate}
                                  onChange={handleCardInputChange}
                                  placeholder="MM/YY"
                                  maxLength="5"
                                />
                              </div>
                              <div>
                                <Label htmlFor="cvv">CVV</Label>
                                <Input
                                  id="cvv"
                                  name="cvv"
                                  type="password"
                                  value={cardDetails.cvv}
                                  onChange={handleCardInputChange}
                                  placeholder="123"
                                  maxLength="4"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label htmlFor="billingAddress">Billing Address (optional)</Label>
                              <Input
                                id="billingAddress"
                                name="billingAddress"
                                value={customerInfo.address}
                                readOnly
                                className="bg-gray-50"
                                placeholder="Same as customer address"
                              />
                            </div>
                          </div>
                          
                          <Button
                            onClick={handleCardPayment}
                            disabled={loading}
                            className="w-full mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 rounded-lg text-lg font-semibold"
                          >
                            {loading ? (
                              <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                Processing...
                              </div>
                            ) : (
                              `Pay Now - $${amount}`
                            )}
                          </Button>
                          
                          <div className="mt-4 text-center">
                            <p className="text-xs text-gray-500">
                              <Check className="w-3 h-3 inline text-green-600 mr-1" />
                              Your payment is secure and encrypted
                            </p>
                            {paymentMethod === 'paypal' && (
                              <p className="text-xs text-blue-600 mt-2">
                                Processing via PayPal
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-start mt-8">
                    <Button
                      onClick={handlePreviousStep}
                      variant="outline"
                      className="px-8 py-3 rounded-full text-lg"
                      disabled={loading}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
